<template>
  <div>
    <div class="sm:fe-hidden">
      <JdsDialog @close="$emit('close')">
        <template #header>
          <div class="fe-text-heading-xxs lg:fe-text-heading-xs">
            {{ questionData.question }}
          </div>
        </template>

        <template #body>
          <div class="fe-h-[1px] fe-bg-primary-grey-40 fe-mb-m fe-mt-s"></div>
          <div v-if="!!allAnswers" class="fe-overflow-y-auto">
            <div v-if="allAnswers?.length">
              <div v-for="answer in allAnswers" :key="answer.id">
                <div class="fe-flex fe-items-center">
                  <UserInitialsAvatar :name="answer.customerName" />

                  <div
                    class="fe-text-body-xxs-bold lg:fe-text-body-s-bold fe-mx-xs lg:fe-ml-xs lg:fe-mr-s"
                  >
                    {{ answer.customerName }}
                  </div>

                  <div
                    class="fe-flex fe-items-center fe-gap-xxxs fe-text-primary-grey-60 fe-text-body-xxs"
                  >
                    <JdsIcon
                      name="SuccessIcon"
                      size="s"
                      color="fe-text-primary-grey-60"
                    />
                    Verified User
                  </div>
                </div>
                <div
                  class="fe-ml-l fe-text-primary-grey-80 fe-text-body-xxs lg:fe-text-body-xs"
                >
                  A. {{ answer.answer }}
                </div>
                <div
                  class="fe-ml-l fe-mt-s fe-mb-m lg:fe-mt-base lg:fe-mb-xxl fe-flex fe-items-center"
                >
                  <QnAVote
                    :answer="answer"
                    :disabled="isAnswerCreatedBySameUser(answer)"
                  />
                </div>
              </div>
            </div>

            <div v-else>
              <EmptyQnA text="No answers available for this question." />

              <div class="fe-h-[1px] fe-bg-primary-grey-40 fe-my-m"></div>
            </div>
          </div>
          <div v-if="!isQuestionCreatedBySameUser">
            <div class="fe-text-body-m-bold">Answer this question</div>
            <div
              class="fe-flex fe-justify-between fe-items-center fe-text-body-s fe-text-primary-grey-60 fe-mb-base"
            >
              <div>Your answer will be reviewed and posted in 1-2 days.</div>
              <div>{{ answerHelperText }}</div>
            </div>
            <div>
              <JdsTextArea
                placeholder="Type your answer here.."
                :required="true"
                :min-length="answerConfig?.content?.minCharLimit"
                :max-length="answerConfig?.content?.maxCharLimit"
                :value="answerText"
                @input="handleAnswerText"
                :state="answerTextError ? 'error' : 'none'"
                :state-text="answerTextError"
                textAreaClassname="fe-min-h-[96px]"
                :disabled="isQuestionCreatedBySameUser"
              />
            </div>
          </div>
        </template>

        <template #footer v-if="!isQuestionCreatedBySameUser">
          <div>
            <JdsButton
              size="medium"
              variant="primary"
              :fullWidth="true"
              :disabled="!!answerTextError || !answerText"
              @click="handleSubmit"
            >
              Submit
            </JdsButton>
          </div>
        </template>
      </JdsDialog>
    </div>

    <div class="lg:fe-hidden">
      <JdsSidePanel @close="$emit('close')">
        <template #header>
          <div class="fe-w-full fe-flex fe-justify-between fe-items-center">
            <div>More Answers</div>
            <div>
              <JdsButton
                variant="secondary"
                size="small"
                class-name="fe-text-white"
                @click="focusText"
              >
                Answer
              </JdsButton>
            </div>
          </div>
        </template>

        <template #body>
          <div
            class="fe-shadow-drop fe-mx-[-24px] fe-px-m fe-py-s fe-mb-s fe-text-body-xs-bold"
          >
            Q. {{ questionData.question }}
          </div>
          <div v-if="!!allAnswers" class="fe-overflow-y-auto">
            <div v-if="allAnswers?.length">
              <div v-for="answer in allAnswers" :key="answer.id">
                <div class="fe-flex fe-items-center">
                  <UserInitialsAvatar :name="answer.customerName" />

                  <div
                    class="fe-text-body-xxs-bold lg:fe-text-body-s-bold fe-mx-xs lg:fe-ml-xs lg:fe-mr-s"
                  >
                    {{ answer.customerName }}
                  </div>

                  <div
                    class="fe-flex fe-items-center fe-gap-xxxs fe-text-primary-grey-60 fe-text-body-xxs"
                  >
                    <JdsIcon
                      name="SuccessIcon"
                      size="s"
                      color="fe-text-primary-grey-60"
                    />
                    Verified User
                  </div>
                </div>
                <div
                  class="fe-ml-l fe-text-primary-grey-80 fe-text-body-xxs lg:fe-text-body-xs"
                >
                  A. {{ answer.answer }}
                </div>
                <div
                  class="fe-ml-l fe-mt-s fe-mb-m lg:fe-mt-base lg:fe-mb-xxl fe-flex fe-items-center"
                >
                  <QnAVote
                    :answer="answer"
                    :disabled="isAnswerCreatedBySameUser(answer)"
                  />
                </div>
              </div>
            </div>

            <div v-else>
              <EmptyQnA text="No answers available for this question." />

              <div class="fe-h-[1px] fe-bg-primary-grey-40 fe-my-m"></div>
            </div>
          </div>
        </template>

        <template #footer v-if="!isQuestionCreatedBySameUser">
          <div class="fe-flex fe-flex-col fe-gap-xs">
            <div class="fe-text-body-xs">Answer this question</div>
            <div
              class="fe-flex fe-justify-between fe-items-center fe-text-body-xxs fe-text-primary-grey-60"
            >
              <div>Your answer will be reviewed and posted in 1-2 days.</div>
              <div>{{ answerHelperText }}</div>
            </div>
            <div>
              <JdsTextArea
                ref="textAreaComponent"
                placeholder="Type your answer here.."
                :required="true"
                :min-length="answerConfig?.content?.minCharLimit"
                :max-length="answerConfig?.content?.maxCharLimit"
                :value="answerText"
                @input="handleAnswerText"
                :state="answerTextError ? 'error' : 'none'"
                :state-text="answerTextError"
                textAreaClassname="fe-min-h-[96px]"
                :disabled="isQuestionCreatedBySameUser"
              />
            </div>
            <div>
              <JdsButton
                size="medium"
                variant="primary"
                :fullWidth="true"
                :disabled="!!answerTextError || !answerText"
                @click="handleSubmit"
              >
                Submit
              </JdsButton>
            </div>
          </div>
        </template>
      </JdsSidePanel>
    </div>
  </div>
</template>

<script>
  import JdsButton from "@/components/DesignSystem/JdsButton/JdsButton.vue"
  import JdsDialog from "@/components/DesignSystem/JdsDialog/JdsDialog.vue"
  import JdsIcon from "@/components/DesignSystem/JdsIcon/JdsIcon.vue"
  import JdsTextArea from "@/components/DesignSystem/JdsTextArea/JdsTextArea.two.vue"
  import UserInitialsAvatar from "@/components/QuestionAndAnswer/UserInitialsAvatar/UserInitialsAvatar.vue"
  import QnAVote from "@/components/QuestionAndAnswer/QnAVote/QnAVote.vue"
  import { modalMixin } from "@/mixins/modalMixin"
  import { toastMixin } from "@/mixins/toastMixin"
  import {
    apiAddAnswer,
    apiGetAllAnswersForQuestion,
  } from "@/api_service/qnaApi"
  import EmptyQnA from "@/components/QuestionAndAnswer/EmptyQnA/EmptyQnA.vue"
  import JdsSidePanel from "@/components/DesignSystem/JdsSidePanel/JdsSidePanel.vue"

  export default {
    name: "AnswerDialog",
    components: {
      JdsButton,
      JdsDialog,
      JdsTextArea,
      JdsIcon,
      QnAVote,
      EmptyQnA,
      UserInitialsAvatar,
      JdsSidePanel,
    },
    mixins: [modalMixin, toastMixin],
    inject: ["globalState", "checkVerifiedPurchase"],
    props: {
      qna: {
        type: Object,
        default: () => ({}),
        required: true,
      },
    },
    data() {
      return {
        answerText: "",
        answerTextError: "",
        loading: false,
        allAnswers: null,
      }
    },
    created() {
      if (this.questionData?.numberOfAnswers > 1) {
        this.getAllAnswersForQuestion()
      } else {
        this.allAnswers = this.qna.answers
      }
    },
    watch: {
      answerText() {
        if (this.answerTextError) {
          if (this.validateAnswerText()) {
            this.answerTextError = ""
          }
        }
      },
    },
    computed: {
      questionData() {
        return this.qna.question
      },
      answerConfig() {
        return this.globalState.widgetConfig?.answer_properties
      },
      answerHelperText() {
        const maxCharLength = this.answerConfig?.content?.maxCharLimit
        return `${this.answerText.length}/${maxCharLength}`
      },
      productData() {
        return this.globalState.product
      },
      isQuestionCreatedBySameUser() {
        return this.globalState.user.user_id === this.questionData?.createdBy
      },
    },
    methods: {
      isAnswerCreatedBySameUser(answer) {
        return this.globalState.user.user_id === answer?.createdBy
      },
      async getAllAnswersForQuestion() {
        try {
          const questionId = this.questionData?.id
          const response = await apiGetAllAnswersForQuestion(questionId)

          this.allAnswers = response?.data?.answer
        } catch (error) {
          console.log(error)
        }
      },
      handleAnswerText(answerText) {
        this.answerText = answerText
      },
      validateAnswerText() {
        const config = this.answerConfig?.content

        const charLength = this.answerText.length

        const { minCharLimit = 0, maxCharLimit = 0 } = config

        // If content is hidden, then no need to check for validity
        // If content string is empty and it is not required (as per config), then no need to check for validity
        if (config.hidden || (!config.required && !charLength)) {
          return true
        }

        // If content is filled by user, then it doesn't matter, if it is required or not, it should met min and max character guideline
        if (
          charLength &&
          charLength >= minCharLimit &&
          charLength <= maxCharLimit
        ) {
          return true
        }

        const errorMessages = {
          contentError: "Please provide your answer",
          minError: `Minimum ${minCharLimit} characters required`,
          maxError: `Maximum ${minCharLimit} characters required`,
        }

        if (!charLength) {
          this.answerTextError = errorMessages.contentError
        } else if (charLength < minCharLimit) {
          this.answerTextError = errorMessages.minError
        } else if (charLength > maxCharLimit) {
          this.answerTextError = errorMessages.maxError
        }

        return false
      },
      async handleSubmit() {
        try {
          this.loading = true

          const isUserAllowedToAnswer = await this.checkVerifiedPurchase()

          if (!isUserAllowedToAnswer) {
            this.showToast({
              message: "Your Answer could not be added",
              description:
                "Contribution to the answers for this product requires a prior purchase.",
              type: "warning",
            })
            this.loading = false
            return
          }

          if (!this.validateAnswerText()) {
            this.loading = false
            return
          }

          const requestData = {
            productId: this.productData.productId,
            productURL: this.productData.productURL,
            alternateCode1: this.productData.alternateCode1,
            alternateCode2: this.productData.alternateCode2,
            categoryL1: this.productData.categoryL1,
            categoryL2: this.productData.categoryL2,
            categoryL3: this.productData.categoryL3,
            categoryL4: this.productData.categoryL4,
            answer: this.answerText.trim(),
            question: this.qna.question.question,
            questionId: this.qna.question.id,
          }

          const response = await apiAddAnswer(requestData)

          this.loading = false

          this.$emit("close")
          this.showToast("Answer Submitted", "success")

          return response?.data
        } catch (error) {
          this.loading = false
          this.showToast(error?.message, "error")
        }
      },
      focusText() {
        this.$refs.textAreaComponent.focusTextarea
      },
    },
  }
</script>
